import React from "react";
import { connect } from "react-redux";
import { buttonVariants } from "../../ui/button";
import {
  getDateTimeFormate,
  formatMoney,
  Currency,
  getIsOssino,
  convertToUSD,
  getConvertedAmountWithCurrency,
  getBoostedOdds,
} from "../../sharedfiles/helper";
import BetId from "../BetId/BetId";
import {
  setBetId,
  setOpenDialog,
  betIdRecords,
} from "../BetHistory/betHistorySlice";
import { getBetAuditDetails } from "./playerBetHistorySlice";
import Table from "../Common/table/Table";
import { auditTableHeaders } from "../Common/data/mapData";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import Tooltips from "../Common/Tooltip";
import { betAudit, betHistoryInfo } from "../../sharedfiles/tooltipMsgs"
import { ArrowUp, ArrowDown, TrendingUp } from "lucide-react";
import NoDataFound from "../Common/table/NoDataFound";
import { Table as ShadcnTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { cn } from "../../lib/utils";
import { useColumnPreferences } from "../../sharedfiles/hooks/useColumnPreferences";
import TableToolbar from "../Common/table/TableToolbar";

const BettingHistoryTableBody = ({ sortedData, showPermission, ascending, onToggleSort, onOpenDialog, onCallEventDetails }) => {
  const headerConfigs = [
    { label: "Bet ID", tooltip: betHistoryInfo.BetId, align: "text-center" },
    { label: "Booking Code", tooltip: betHistoryInfo.bookingCode, align: "text-center" },
    { label: "Short Bet ID", tooltip: betHistoryInfo.shorbetId, align: "text-center" },
    { label: "Event Name", tooltip: betHistoryInfo.eventName, align: "text-center" },
    { label: "Bet Status", tooltip: betHistoryInfo.betStatus, align: "text-center" },
    { label: "Bet Decision Status", tooltip: betHistoryInfo.betDecisionStatus, align: "text-center" },
    { label: "Bet Decision Reviewer Name", tooltip: betHistoryInfo.betDecisionReviewerName, align: "text-center" },
    { label: "Bet Decision Reason", tooltip: betHistoryInfo.betDecisionReason, align: "text-center" },
    { label: "Rejection Code", tooltip: betHistoryInfo.rejection, align: "text-center" },
    { label: "Placed On", tooltip: betHistoryInfo.Placed_On, align: "text-center", sortable: true },
    { label: "Settled On", tooltip: betHistoryInfo.Settled_On, align: "text-center" },
    { label: "Betfold", tooltip: betHistoryInfo.Betfold, align: "text-center" },
    { label: "Bet Type", tooltip: betHistoryInfo.BetType, align: "text-center" },
    { label: `Stake (${Currency()})`, tooltip: betHistoryInfo.Stake, align: "text-right" },
    { label: `Total Payout (${Currency()})`, tooltip: betHistoryInfo.Total_Payout, align: "text-right" },
    { label: `Potential Payout (${Currency()})`, tooltip: betHistoryInfo.Potential_Payout, align: "text-right" },
    ...(showPermission ? [
      { label: "Stake Tax %", tooltip: betHistoryInfo.Stake_Tax_Percentage, align: "text-right" },
      { label: `Stake After Tax (${Currency()})`, tooltip: betHistoryInfo.Stake_AfterTax, align: "text-right" },
      { label: `Stake Tax Amount (${Currency()})`, tooltip: betHistoryInfo.Stake_TaxAmount, align: "text-right" },
      { label: "Winnings Tax %", tooltip: betHistoryInfo.Winnings_Tax_Percentage, align: "text-right" },
      { label: `Winnings Tax Amount (${Currency()})`, tooltip: betHistoryInfo.Winnings_TaxAmount, align: "text-right" },
    ] : []),
    { label: `Total Payout Without Bonus (${Currency()})`, tooltip: betHistoryInfo.Total_Payout_Without_Bonus, align: "text-right" },
    { label: `Total Payout With Bonus (${Currency()})`, tooltip: betHistoryInfo.Total_PayoutWithBonus, align: "text-right" },
    { label: `Bonus Max Win Amount (${Currency()})`, tooltip: betHistoryInfo.Bonus_MaxWinAmount, align: "text-right" },
    { label: "Bonus Max Win %", tooltip: betHistoryInfo.Bonus_MaxWinPercentage, align: "text-right" },
    { label: "Place Bet Type", tooltip: betHistoryInfo.Place_BetType, align: "text-center" },
    { label: "Audit", tooltip: betHistoryInfo.Audit, align: "text-center" },
  ];
  const headers = headerConfigs.map((c) => c.label);

  const cellClassName = [
    "text-center",
    "text-center text-xs font-mono",
    "text-center text-sm",
    "text-center text-sm max-w-[200px] truncate",
    "text-center",
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm whitespace-nowrap",
    "text-center text-sm whitespace-nowrap",
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm font-semibold",
    "text-center text-sm",
    "text-center text-sm",
    ...(showPermission ? [
      "text-center text-sm",
      "text-center text-sm",
      "text-center text-sm",
      "text-center text-sm",
      "text-center text-sm",
    ] : []),
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm",
    "text-center text-sm",
    "text-center",
  ];

  const rowAccessors = [
    (item) => (
      <span
        className='text-primary underline cursor-pointer hover:text-primary/80 text-xs font-mono'
        onClick={() => onOpenDialog(item.betId, getConvertedAmountWithCurrency(item.potentialPayout, item.currencyCode || item.currency, item.baseCurrencyRate))}
      >
        {item.betId}
      </span>
    ),
    (item) => item.bookingCode || '-',
    (item) => item.shortBetId,
    (item) => item.fixtureName,
    (item) => {
      const s = item.betStatus?.toUpperCase();
      const cls =
        s === 'WON' ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-400' :
          s === 'LOST' ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-400' :
            s === 'OPEN' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-400' :
              s === 'RUNNING' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-400' :
                s === 'CASHOUT' ? 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-400' :
                  s === 'REJECTED' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-400' :
                    s === 'VOID' || s === 'CANCELLED' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-400';
      return (
        <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", cls)}>
          {item.betStatus}
        </span>
      );
    },
    (item) => {
      const s = item.betDecisionStatus?.toUpperCase();
      if (!s) return '-';
      const cls =
        s === 'ACCEPTED' ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-400' :
          s === 'REJECTED' ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-400' :
            s === 'PENDING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-400' :
              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      return (
        <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", cls)}>
          {item.betDecisionStatus}
        </span>
      );
    },
    (item) => item.betDecisionReviewerName || '-',
    (item) => item.betDecisionReason || '-',
    (item) => item.rejectionCode || '-',
    (item) => getDateTimeFormate(item.placedDate),
    (item) => (item.settledDate ? getDateTimeFormate(item.settledDate) : '-'),
    (item) => item.betFold,
    (item) => item.betType,
    (item) => getConvertedAmountWithCurrency(item.stake, item.currencyCode || item.currency, item.baseCurrencyRate),
    (item) => getConvertedAmountWithCurrency(item.payout, item.currencyCode || item.currency, item.baseCurrencyRate),
    (item) => getConvertedAmountWithCurrency(item.potentialPayout, item.currencyCode || item.currency, item.baseCurrencyRate),
    ...(showPermission ? [
      (item) => item.stakeTax,
      (item) => getConvertedAmountWithCurrency(item.stakeAfterTax, item.currencyCode || item.currency, item.baseCurrencyRate),
      (item) => getConvertedAmountWithCurrency(item.stakeTaxAmount, item.currencyCode || item.currency, item.baseCurrencyRate),
      (item) => item.winningsTax,
      (item) => getConvertedAmountWithCurrency(item.winningsTaxAmount, item.currencyCode || item.currency, item.baseCurrencyRate),
    ] : []),
    (item) => getConvertedAmountWithCurrency(item.totalPayout, item.currencyCode || item.currency, item.baseCurrencyRate),
    (item) => getConvertedAmountWithCurrency(item.totalPayoutWithBonus, item.currencyCode || item.currency, item.baseCurrencyRate),
    (item) => getConvertedAmountWithCurrency(item.bonusMaxWinAmount, item.currencyCode || item.currency, item.baseCurrencyRate),
    (item) => item.bonusMaxWinPercentage || 0,
    (item) => item.placeBetType,
    (item) => (
      <span
        className={cn(buttonVariants({ variant: "default", size: "sm" }), "cursor-pointer")}
        onClick={() => onCallEventDetails(item.betId)}
      >
        Bet Audit
      </span>
    ),
  ];

  const { visibleIndices, allColumns, toggleVisibility, reorder, resetToDefault } =
    useColumnPreferences("bettingHistoryBettingHistoryTable", headers);

  return (
    <div className='relative rounded-lg border bg-card shadow-sm overflow-hidden'>
      <TableToolbar
        columns={allColumns}
        onToggle={toggleVisibility}
        onReorder={reorder}
        onReset={resetToDefault}
      />
      <ShadcnTable scrollKey="bettingHistoryBettingHistoryTable">
        <TableHeader>
          <TableRow>
            {visibleIndices.map((origIdx) => {
              const c = headerConfigs[origIdx];
              return c.sortable ? (
                <TableHead
                  key={origIdx}
                  className={cn(c.align, "whitespace-nowrap cursor-pointer select-none hover:bg-muted/50")}
                  onClick={onToggleSort}
                >
                  <div className='flex justify-center items-center gap-1'>
                    <span>{c.label}</span>
                    <Tooltips info={c.tooltip} />
                    {ascending ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                  </div>
                </TableHead>
              ) : (
                <TableHead key={origIdx} className={cn(c.align, "whitespace-nowrap")}>
                  <div className={cn("flex items-center gap-1", c.align === "text-right" ? "justify-end" : "justify-center")}>
                    <span>{c.label}</span>
                    <Tooltips info={c.tooltip} />
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData && Array.isArray(sortedData) && sortedData.length > 0 ? (
            sortedData.map((item, index) => (
              <TableRow key={index}>
                {visibleIndices.map((origIdx) => (
                  <TableCell key={origIdx} className={cellClassName[origIdx]}>
                    {rowAccessors[origIdx](item)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <NoDataFound colspan={visibleIndices.length} />
          )}
        </TableBody>
      </ShadcnTable>
    </div>
  );
};

class BettingHistoryTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      asceending: true,
      userData: [],
      betId: "",
      betAuditData: [],
      openEventBox: false,
      openBetId: false,
      potentialPayout: "",
    };
    this.dateSorting = this.dateSorting.bind(this);
    this.openDialogHandler = this.openDialogHandler.bind(this);
    this.callEventDetails = this.callEventDetails.bind(this);
    this.updateDataToDialog = this.updateDataToDialog.bind(this);
  }

  dateSorting() {
    const getchedData = [...this.props.data];
    const sortData = getchedData.sort((a, b) => {
      const c = new Date(a.transactionDate);
      const d = new Date(b.transactionDate);
      return c - d;
    });
    return this.state.asceending ? sortData : sortData.reverse();
  }
  openDialogHandler(id, potentialPayout) {
    this.props.dispatch(setOpenDialog(true));
    this.props.dispatch(betIdRecords(id));
    this.setState({ potentialPayout: potentialPayout });
  }
  onDialogClose() {
    this.setState({ openEventBox: false });
  }
  callEventDetails = (userId) => {
    this.setState({ betAuditData: [] });
    if (userId !== 0) {
      this.props.dispatch(getBetAuditDetails(userId));
      this.setState({ betId: userId });
      this.setState({ openEventBox: true });
    }
  };
  updateDataToDialog() {
    const data = this.props.betAudit;
    const tBody =
      data &&
        data !== undefined &&
        data !== null &&
        Object.keys(data).length > 0 &&
        Array.isArray(data) &&
        data.length > 0
        ? data.map((item) => [
          item.userId,
          item.betSelection,
          item.betStatus,
          (() => {
            const hasBoost = item?.odds != null && item?.boostedOddsPercentage != null && Number(item.boostedOddsPercentage) !== 0;
            if (!hasBoost) return item.odds;
            const boosted = getBoostedOdds(item.odds, item.boostedOddsPercentage);
            return (
              <span
                className="inline-flex items-center gap-1"
                title={`Boosted +${item.boostedOddsPercentage}% (base ${item.odds})`}
              >
                {boosted.toFixed(2)}
                <TrendingUp className="h-3.5 w-3.5" />
              </span>
            );
          })(),
          getConvertedAmountWithCurrency(item.payout, item.currencyCode || item.currency, item.baseCurrencyRate),
          ...(getIsOssino() ? [item.payoutUsd ?? "-"] : []),
          getDateTimeFormate(item.updatedDate),
        ])
        : [];
    const tableData = [auditTableHeaders, betAudit, ...tBody];
    this.setState({ betAuditData: tableData });
  }
  hadleBetId(val) {
    this.setState({ openBetId: val });
  }

  componentDidUpdate(prevProps) {
    if (this.props.betAudit !== prevProps.betAudit) {
      this.updateDataToDialog();
    }
  }

  componentDidMount() {
    this.hadleBetId(true);
  }
  componentWillUnmount() {
    this.hadleBetId(false);
  }

  render() {
    const showPermission = this.props.stakeTaxPermit && this.props.winTaxPermit;
    return (
      <>
        {this.state.openEventBox ? (
          <Dialog open={this.state.openEventBox} onOpenChange={() => this.onDialogClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">Bet Event Details - {this.state.betId}</DialogTitle>
              </DialogHeader>
              <div className='space-y-4 overflow-y-auto flex-1 min-h-0'>
                <Table tableData={this.state.betAuditData} hLen={4} vLen={2} withCard={true} />
              </div>
            </DialogContent>
          </Dialog>
        ) : null}
        {this.state.openBetId ? (
          <BetId
            data={
              this.props.betIdRecords.length > 0 ? this.props.betIdRecords : []
            }
            setBack={setBetId}
            potentialPayout={this.state.potentialPayout}
            enableDownload={this.props.enableDownload}
            pdfTitle={this.props.pdfTitle}
            fileName={this.props.fileName}
          />) : null
        }
        <BettingHistoryTableBody
          sortedData={this.props?.data && Array.isArray(this.props?.data) && this.props?.data.length > 0 ? this.dateSorting() : []}
          showPermission={showPermission}
          ascending={this.state.asceending}
          onToggleSort={() => this.setState({ asceending: !this.state.asceending })}
          onOpenDialog={(id, potentialPayout) => this.openDialogHandler(id, potentialPayout)}
          onCallEventDetails={(betId) => this.callEventDetails(betId)}
        />
      </>
    );
  }
}
function mapStateToProps(state) {
  return {
    displayValue: state.sidebar.displayValue,
    betIdRecords: state.betHistorySliceReducer.betIdRecords,
    betAudit: state.playerBetHistorySlice.betAudit,
    stakeTaxPermit: state.dashboard.stakeTaxPermission,
    winTaxPermit: state.dashboard.winTaxPermission,
  };
}
export default connect(mapStateToProps)(BettingHistoryTable);

